import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import type { AiModelUsage } from "@backstory/protocol";
import { OrgLayout } from "../../../components/Shell";
import { ErrorState, Skeleton } from "../../../components/States";
import { Meter, PageHeader, fmtNum, fmtUsd } from "../../../components/ui";
import { formatDateTime } from "../../../lib/format";
import { aiApi, aiKeys } from "../api";
import "../ai.css";

const FUNDING_LABEL: Record<AiModelUsage["funding"], string> = {
  platform: "Credits",
  byok: "Your own key",
  included: "Included",
};

export function AiUsagePage() {
  const { org } = useParams({ strict: false }) as { org: string };
  const q = useQuery({ queryKey: aiKeys.usage(), queryFn: () => aiApi.usage(), retry: false });
  if (q.isPending) return <OrgLayout orgId={org}><Skeleton lines={8} /></OrgLayout>;
  if (q.isError) return <OrgLayout orgId={org}><ErrorState error={q.error} retry={() => void q.refetch()} /></OrgLayout>;
  const u = q.data;
  const c = u.credits;
  // Model identifiers are routing internals, not customer-facing attribution.
  // Usage is therefore rolled up by provider and funding source.
  const byProvider = [...u.byModel.reduce((rows, row) => {
    const key = `${row.provider}\0${row.funding}`;
    const current = rows.get(key) ?? { provider: row.provider, funding: row.funding, calls: 0, tokens: 0, billedUsd: 0 };
    current.calls += row.calls;
    current.tokens += row.tokens;
    current.billedUsd += row.billedUsd;
    rows.set(key, current);
    return rows;
  }, new Map<string, Pick<AiModelUsage, "provider" | "funding" | "calls" | "tokens" | "billedUsd">>()).values()];
  return (
    <OrgLayout orgId={org}>
      <PageHeader title="AI usage" subtitle={`${formatDateTime(u.from)} → ${formatDateTime(u.to)}`} actions={<Link to="/orgs/$org/ai" params={{ org }} className="btn">AI settings</Link>} />
      <div className="card section">
        <Meter label="AI credits" used={c.usedUsd} included={c.includedUsd} projected={c.projectedUsd} unit="" format={fmtUsd} />
        <p className="small muted" style={{ margin: "6px 0 0" }} data-testid="credits-line">
          {fmtUsd(c.remainingUsd)} available
          {c.overageUsd > 0 && <> · {fmtUsd(c.overageUsd)} used after the plan allowance</>}
          {c.reloadBalanceUsd > 0 && <> · {fmtUsd(c.reloadBalanceUsd)} prepaid balance</>}
          {c.shadow && <> · preview only, not yet billed</>}
        </p>
        {c.remainingUsd <= 0 && (
          <p className="small" style={{ marginBottom: 0 }}>
            Platform-funded AI is paused. <Link to="/orgs/$org/billing" params={{ org }}>Add prepaid credits</Link> or connect your own provider.
          </p>
        )}
      </div>
      <div className="usage-grid" style={{ marginTop: 16 }}>
        <div className="usage-tile" data-testid="platform-funded">
          <div className="n">{fmtUsd(c.platformUsd)}</div>
          <div className="l">Charged as credits · {fmtNum(c.platformCalls)} calls</div>
        </div>
        <div className="usage-tile" data-testid="own-key-funded">
          <div className="n">{fmtUsd(c.ownKeyUsd)}</div>
          <div className="l">On your own key · {fmtNum(c.ownKeyCalls)} calls, no credits</div>
        </div>
        <div className="usage-tile" data-testid="fallback-charges">
          <div className="n">{fmtUsd(c.fallbackUsd)}</div>
          <div className="l">After a provider failed · {fmtNum(c.fallbackCalls)} calls</div>
        </div>
        <div className="usage-tile"><div className="n">{fmtNum(u.deepAnalysesUsed)}</div><div className="l">Deep analyses</div></div>
        <div className="usage-tile"><div className="n">{fmtNum(u.chatTurns)}</div><div className="l">Ask Why turns</div></div>
        <div className="usage-tile"><div className="n">{fmtNum(u.automatic.summaries + u.automatic.clusterExplanations + u.automatic.digests)}</div><div className="l">Automatic AI, included</div></div>
      </div>
      <div className="card section" style={{ marginTop: 16 }}>
        <h3 style={{ marginTop: 0 }}>Where the credits went</h3>
        <table className="table" aria-label="AI usage by provider">
          <thead>
            <tr>
              <th>Provider</th><th>Paid by</th>
              <th style={{ textAlign: "right" }}>Calls</th>
              <th style={{ textAlign: "right" }}>Tokens</th>
              <th style={{ textAlign: "right" }}>Credits</th>
            </tr>
          </thead>
          <tbody>
            {byProvider.map((m) => (
              <tr key={`${m.provider}:${m.funding}`} className="static">
                <td>{m.provider.replace(/^byok:/, "")}</td>
                <td>{FUNDING_LABEL[m.funding]}</td>
                <td className="mono" style={{ textAlign: "right" }}>{fmtNum(m.calls)}</td>
                <td className="mono" style={{ textAlign: "right" }}>{fmtNum(m.tokens)}</td>
                <td className="mono" style={{ textAlign: "right" }}>{m.funding === "platform" ? fmtUsd(m.billedUsd) : "—"}</td>
              </tr>
            ))}
            {byProvider.length === 0 && <tr className="static"><td colSpan={5} className="muted" style={{ textAlign: "center", padding: 12 }}>No AI calls yet this period.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="card section" style={{ marginTop: 16 }}>
        <h3 style={{ marginTop: 0 }}>Who used AI</h3>
        <p className="small muted">Ask Why, a summary someone ran again, and a deep analysis are listed under that person. Work the product runs on its own is listed as System.</p>
        <div className="stack-table">
          <table className="table" aria-label="AI credits by person">
            <thead>
              <tr>
                <th>Person</th>
                <th style={{ textAlign: "right" }}>Ask Why</th>
                <th style={{ textAlign: "right" }}>Summaries</th>
                <th style={{ textAlign: "right" }}>Deep analyses</th>
                <th style={{ textAlign: "right" }}>Other</th>
                <th style={{ textAlign: "right" }}>Credits</th>
              </tr>
            </thead>
            <tbody>
              {u.byUser.map((b) => (
                <tr key={b.userId} className="static">
                  <td data-label="Person">{b.email || "A team member"}</td>
                  <td data-label="Ask Why" className="mono" style={{ textAlign: "right" }}>{fmtUsd(b.askWhyUsd)}</td>
                  <td data-label="Summaries" className="mono" style={{ textAlign: "right" }}>{fmtUsd(b.summariesUsd)}</td>
                  <td data-label="Deep analyses" className="mono" style={{ textAlign: "right" }}>{fmtUsd(b.analysesUsd)}</td>
                  <td data-label="Other" className="mono" style={{ textAlign: "right" }}>{fmtUsd(b.otherUsd)}</td>
                  <td data-label="Credits" className="mono" style={{ textAlign: "right" }}>{fmtUsd(b.creditsUsd)}</td>
                </tr>
              ))}
              {u.byUser.length === 0 && (
                <tr className="static"><td colSpan={6} className="muted" style={{ textAlign: "center", padding: 12 }}>No one has started an AI action yet.</td></tr>
              )}
              <tr className="static" data-testid="system-usage">
                <td data-label="Person">System</td>
                <td data-label="Ask Why" className="mono" style={{ textAlign: "right" }}>{fmtUsd(u.system.askWhyUsd)}</td>
                <td data-label="Summaries" className="mono" style={{ textAlign: "right" }}>{fmtUsd(u.system.summariesUsd)}</td>
                <td data-label="Deep analyses" className="mono" style={{ textAlign: "right" }}>{fmtUsd(u.system.analysesUsd)}</td>
                <td data-label="Other" className="mono" style={{ textAlign: "right" }}>{fmtUsd(u.system.otherUsd)}</td>
                <td data-label="Credits" className="mono" style={{ textAlign: "right" }}>{fmtUsd(u.system.creditsUsd)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </OrgLayout>
  );
}
